import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** A role as returned by the backend. */
export interface Role {
  id: number;
  name: string;
  description?: string | null;
  status?: string | null;
}

/** A permission offered on the role screen (fields are best-effort — the API shape may vary). */
export interface Permission {
  id: number;
  name?: string | null;
  codename?: string | null;
  description?: string | null;
  /** Optional grouping/module fields used to bucket permissions on the create screen. */
  group?: string | null;
  module?: string | null;
  category?: string | null;
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/** Normalized paginated list the roles table consumes. */
export interface RolesResponse {
  items: Role[];
  total: number;
  page: number;
  pageSize: number;
}

/** Roles & permissions endpoints, injected into the shared `baseApi`. */
export const rolesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getRoles: build.query<
      RolesResponse,
      { search?: string; page?: number; pageSize?: number; all?: boolean } | void
    >({
      query: (arg) => ({
        url: `${ApiEndPoints.role}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          // `all` fetches the full list (no pagination) — used for dropdowns.
          ...(arg?.all ? {} : { page: arg?.page ?? 1, page_size: arg?.pageSize ?? 10 }),
        },
      }),
      transformResponse: (r: Listish<Role>, _meta, arg) => {
        const items = asList(r);
        const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
        return { items, total, page: arg?.page ?? 1, pageSize: arg?.pageSize ?? 10 };
      },
      providesTags: ['Roles'],
    }),
    getRole: build.query<Role, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.role}/${id}/` }),
      providesTags: ['Roles'],
    }),
    createRole: build.mutation<Role, { name: string; description?: string }>({
      query: (body) => ({
        url: `${ApiEndPoints.role}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Roles'],
    }),
    updateRole: build.mutation<
      Role,
      { id: number | string; patch: Partial<Pick<Role, 'name' | 'description' | 'status'>> }
    >({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.role}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Roles'],
    }),
    deleteRole: build.mutation<void, number | string>({
      query: (id) => ({
        url: `${ApiEndPoints.role}/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Roles'],
    }),
    getPermissions: build.query<Permission[], void>({
      query: () => ({ url: `${ApiEndPoints.permission}/`, params: { page_size: 500 } }),
      transformResponse: (r: Listish<Permission>) => asList(r),
      providesTags: ['Permissions'],
    }),
    // Attach one permission to a role (called once per selected permission on save).
    createRolePermission: build.mutation<unknown, { role: number; permission: number }>({
      query: (body) => ({
        url: `${ApiEndPoints.rolePermission}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Roles'],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionsQuery,
  useCreateRolePermissionMutation,
} = rolesApi;
